import React, { useState, useMemo } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box"; 
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material"; 
import BusinessIcon from '@mui/icons-material/Business'; 
import { MathJaxContext } from 'better-react-mathjax'; 

// Import Components
import RegistrationForm from "./components/RegistrationForm";
import QuestionTest from "./components/QuestionTest"; 
import AssessmentLanding from "./components/AssessmentLanding";
import AptitudeTest from "./components/AptitudeTest";
import BehaviouralTest from "./components/BehaviouralTest";
import FinalResults from "./components/FinalResults";

// Define constants for fixed elements
const FIXED_HEADER_HEIGHT_PX = 130; 
const FOOTER_HEIGHT_PX = 60; 

// --- MUI Custom Theme Setup ---
const customTheme = createTheme({
    palette: {
        mode: 'light',
        // Define a new dark green color for the header 
        headerGreen: {
            main: '#004d40', // Deep dark green
        },
        primary: {
            main: '#4a148c', // Deep Purple
        },
        secondary: {
            main: '#ff6f00', // Deep Orange
        },
        info: {
            main: '#006064', // Dark Cyan
        },
        background: {
            default: '#f5f5f5', 
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: 'Inter, Arial, sans-serif',
    },
});


// Header Component (Fixed at Top)
const BrandedHeader = () => (
    <Box 
        sx={{ 
            // FULL WIDTH STYLES
            width: '100vw', 
            // ❌ REMOVED: ml: 'calc(-50vw + 50%)', 
            
            textAlign: 'center', 
            py: 2, 
            
            // Fixed Positioning
            position: 'fixed',
            top: 0,
            left: 0, // Ensure it's pinned to the left edge
            zIndex: 1000, 
            height: `${FIXED_HEADER_HEIGHT_PX}px`, 
            
            // Green Background
            backgroundColor: customTheme.palette.headerGreen.main,
            
            // Orange Border and Shadow (at the bottom)
            borderBottom: '4px solid', 
            borderColor: customTheme.palette.secondary.main, 
            boxShadow: 8, 
        }}
    >
        <Typography variant="h3" sx={{ letterSpacing: -1, fontWeight: 700, color: 'white' }}>
            ready4industry
        </Typography>
        <Typography variant="h6" sx={{ color: '#bdbdbd' }}>
            BTech CSE Interview Prep
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#bdbdbd' }}>
            World-class technical assessment for future leaders.
        </Typography>
    </Box>
);

// Footer Component (Fixed at Bottom)
const BrandedFooter = () => (
    <Box 
        sx={{
            // FULL WIDTH STYLES
            width: '100vw',
            // ❌ REMOVED: ml: 'calc(-50vw + 50%)',

            // Fixed Positioning
            position: 'fixed',
            bottom: 0,
            left: 0, // Ensure it's pinned to the left edge
            zIndex: 1000,
            height: `${FOOTER_HEIGHT_PX}px`,

            textAlign: 'center',
            py: 1, 
            
            // Green Background
            backgroundColor: customTheme.palette.headerGreen.main,

            // Orange Border and Shadow (at the top)
            borderTop: '4px solid', 
            borderColor: customTheme.palette.secondary.main,
            boxShadow: 8, 
            
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <Typography variant="caption" sx={{ color: '#bdbdbd', fontSize: '0.8rem' }}>
            &copy; {new Date().getFullYear()} ready4industry | World-class technical assessment.
        </Typography>
    </Box>
);


// --- Main Application Component ---
function App() {
  // State for user registration and test flow
  const [studentInfo, setStudentInfo] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('register');
  
  // State to track completion status for each test
  const [completedTests, setCompletedTests] = useState({
    technical: false,
    aptitude: false,
    behavioural: false,
  });

  // State to store detailed results of each test
  const [testResults, setTestResults] = useState({
    technical: null,
    aptitude: null,
    behavioural: null,
  });

  // FIX: Correctly check if all assessments are complete using useMemo
  const isAssessmentComplete = useMemo(() => {
    return Object.values(completedTests).every(status => status === true);
  }, [completedTests]); 

  // --- Handlers ---
  
  const handleRegister = (info) => {
    setStudentInfo(info);
    setCurrentScreen('landing');
  };

  const handleStartTest = (testId) => {
    setCurrentScreen(testId);
  };
  
  const handleTestComplete = (testId, result) => {
    setCompletedTests(prev => ({ ...prev, [testId]: true }));
    setTestResults(prev => ({ ...prev, [testId]: result }));
    setCurrentScreen('landing');
  };
  
  const handleViewResults = () => {
      setCurrentScreen('results');
  };

  // --- Screen Rendering Logic ---
  const renderScreen = () => {
    if (!studentInfo || currentScreen === 'register') {
      return <RegistrationForm onSubmit={handleRegister} />;
    }

    switch (currentScreen) {
      case 'landing':
        return (
          <AssessmentLanding 
            onStartTest={handleStartTest} 
            completedTests={completedTests}
            isAssessmentComplete={isAssessmentComplete}
            onViewResults={handleViewResults}
          />
        );
      case 'technical':
        return (
          <QuestionTest 
            student={studentInfo} 
            onComplete={(testId, result) => handleTestComplete(testId, result)} 
          />
        );
      case 'aptitude':
        return (
          <AptitudeTest 
            student={studentInfo} 
            onComplete={(testId, result) => handleTestComplete(testId, result)} 
          />
        );
      case 'behavioural':
        return (
          <BehaviouralTest 
            student={studentInfo} 
            onComplete={(testId, result) => handleTestComplete(testId, result)} 
          />
        );
      case 'results':
        return (
          <FinalResults 
            student={studentInfo} 
            results={testResults} 
          />
        );
      default:
        return <Typography color="error">Error: Invalid screen.</Typography>;
    }
  };

  // MathJax Configuration
  const mathJaxConfig = {
    loader: { load: ['[tex]/ams'] },
    tex: { 
      inlineMath: [['$', '$'], ['\\(', '\\)']],
      displayMath: [['$$', '$$'], ['\\[', '\\]']],
      packages: { '[+]': ['ams'] },
    }
  };


  return (
    <ThemeProvider theme={customTheme}>
      {/* 1. Global scroll removal on the body/root element */}
      <CssBaseline 
        styles={{ 
          // Suppress all overflow on the document root
          body: { height: '100vh', overflow: 'hidden' }, 
          '#root': { height: '100vh', overflow: 'hidden' } 
        }}
      />
      <MathJaxContext config={mathJaxConfig}>
        
        <BrandedHeader /> 

        <Container 
            maxWidth="md" 
            sx={{ 
                // Margin below the fixed header
                marginTop: `${FIXED_HEADER_HEIGHT_PX}px`,
                // Height calculation accounts for both fixed header and fixed footer
                height: `calc(100vh - ${FIXED_HEADER_HEIGHT_PX}px - ${FOOTER_HEIGHT_PX}px)`,
                // Suppress inner scrolling
                overflowY: 'hidden', 
                pt: 0,
                pb: 0 
            }}
        >
            <Box sx={{ 
                // Content padding (horizontal) - use responsive padding for mobile
                px: { xs: 2, md: 4 }, 
                // Content padding (vertical) - use responsive padding for mobile
                py: { xs: 1, md: 0 }, // Reduced py for mobile
                flexGrow: 1,
                // Make the inner box content scrollable if it exceeds available space
                overflowY: 'auto',
                // This padding is necessary to clear the fixed footer
                paddingBottom: `${FOOTER_HEIGHT_PX + 24}px !important`, 
                // CRITICAL - Ensure the Box takes the full available height to enable scrolling.
                height: '100%', 
            }}> 
                {renderScreen()}
            </Box>
        </Container>

        <BrandedFooter />
      </MathJaxContext>
    </ThemeProvider>
  );
}

export default App;